import React, { useEffect, useState } from 'react';
import { Button } from '@digital/ui';
import { DatePicker, Modal, Table, Tooltip, Upload } from 'antd';
import moment, { Moment } from 'moment';
import locale from 'antd/es/date-picker/locale/pt_BR';
import { IUploadFile } from '@app/types/core.types';
import ModalComentariosParecerTecnico from './ComentariosParecerTecnico/ModalComentariosParecerTecnico';
import ModalExibicaoArquivos from '../WidgetDocumentacao/ModalExibicaoArquivos';
import { decidirRevisaoParecerTecnicoComprador, getParecerTecnicoRevisao, ISasParecerTecnicoRevisao } from '@app/api/sas/sasProposta';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { ordemCompraIdState, reloadVendorListState, dataLimiteEtapaState, modalAcoesIdState } from '../atoms';
import { EGuidTipoSasDocumento } from '@app/enums/EGuidTipoSasDocumento';

interface Props {
    sasProposta: any;
    readOnly?: boolean;
}

const ModalAcoesCompradorParecerTecnico = ({ sasProposta, readOnly = false }: Props) => {
    const ordemCompraId = useRecoilValue(ordemCompraIdState);
    const [reloadVendorList, setReloadVendorList] = useRecoilState(reloadVendorListState);
    const dataLimiteEtapa = useRecoilValue(dataLimiteEtapaState);
    const setModalAcoesId = useSetRecoilState(modalAcoesIdState);

    const [exibirModal, setExibirModal] = useState(false);
    const [revisaoPendente, setRevisaoPendente] = useState<ISasParecerTecnicoRevisao | null>(null);
    const [comentario, setComentario] = useState('');
    const [fileList, setFileList] = useState<IUploadFile[]>([]);
    const [dataLimiteResposta, setDataLimiteResposta] = useState<Moment | null>(null);

    useEffect(() => {
        if (exibirModal) {
            getParecerTecnicoRevisao(sasProposta.id).then(data => {
                if (data) {
                    const pendente = readOnly
                        ? data[data.length - 1] || null
                        : data.find((r: ISasParecerTecnicoRevisao) => r.aguardandoDecisaoComprador) || null;
                    setRevisaoPendente(pendente);
                }
            });
        }
        return () => { };
    }, [exibirModal]);

    const handleCancel = () => {
        setExibirModal(false);
        setComentario('');
        setFileList([]);
        setDataLimiteResposta(null);
    };

    const handleDecisao = (decisao: 'enviar_fornecedor' | 'enviar_gestor') => {
        if (!revisaoPendente) return;
        if (!comentario || !comentario.trim()) {
            Modal.error({ title: 'Comentário obrigatório', content: 'Preencha o comentário antes de prosseguir.' });
            return;
        }
        if (fileList.length === 0) {
            Modal.error({ title: 'Arquivo obrigatório', content: 'É obrigatório anexar um arquivo antes de prosseguir.' });
            return;
        }
        if (!dataLimiteResposta || !dataLimiteResposta.isValid()) {
            Modal.error({ title: 'Data obrigatória', content: 'Informe a Data Limite de Resposta.' });
            return;
        }
        Modal.confirm({
            title: 'Confirmar decisão',
            content: decisao === 'enviar_fornecedor'
                ? 'Confirma o encaminhamento da revisão ao Fornecedor?'
                : 'Confirma a devolução ao Gestor Técnico para reavaliação?',
            okText: 'Confirmar',
            cancelText: 'Cancelar',
            onOk: () => {
                decidirRevisaoParecerTecnicoComprador({
                    ordemCompraId,
                    sasParecerTecnicoRevisaoId: revisaoPendente.id!,
                    decisao,
                    comentario,
                    arquivos: fileList.map((f: any) => f.originFileObj).filter(Boolean),
                    dataLimiteResposta: dataLimiteResposta ? dataLimiteResposta.format('YYYY-MM-DD') : undefined,
                }).then((data: any) => {
                    if (data && data.valid) {
                        handleCancel();
                        setModalAcoesId('');
                        setReloadVendorList(!reloadVendorList);
                    }
                });
            },
        });
    };

    return (
        <>
            <Tooltip title={readOnly ? 'Visualizar Parecer Técnico' : 'Ações Comprador - Parecer Técnico'}>
                <Button icon={readOnly ? 'commons-search' : 'commons-ok'} onClick={() => setExibirModal(true)} />
            </Tooltip>

            <Modal
                title={readOnly ? 'Parecer Técnico — Visualização' : 'Aguardando sua decisão — Parecer Técnico'}
                visible={exibirModal}
                onCancel={handleCancel}
                footer={null}
                width={600}
            >
                {sasProposta.comentarioResultadoParecerTecnico && (
                    <div style={{ marginBottom: 12 }}>
                        <span style={{ fontWeight: 500 }}>Justificativa do Gestor: </span>
                        <span>{sasProposta.comentarioResultadoParecerTecnico}</span>
                    </div>
                )}

                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ModalComentariosParecerTecnico
                        contemComentario={revisaoPendente ? revisaoPendente.contemComentario : false}
                        id={revisaoPendente ? revisaoPendente.id || '' : ''}
                        sasPropostaId={sasProposta.id}
                        update={() => { }}
                        disabled={true}
                    />
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Chat com Gestor Técnico</span>
                    <ModalExibicaoArquivos
                        propostaId={sasProposta.id}
                        sasTipoDocumentoId={EGuidTipoSasDocumento.PropostaTecnicaConsolidada}
                        hideExcluido={true}
                        disabled={true}
                    />
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Anexo do Gestor</span>
                    <ModalExibicaoArquivos
                        propostaId={sasProposta.id}
                        sasTipoDocumentoId={EGuidTipoSasDocumento.AceiteRecebimentoPropostaTecnicasProponentes}
                        hideExcluido={true}
                        disabled={true}
                    />
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Anexo do Fornecedor</span>
                </div>

                {!readOnly && <div style={{ marginBottom: 12 }}>
                    <Upload
                        multiple
                        showUploadList={false}
                        beforeUpload={(file) => {
                            setFileList(prev => [...prev, { uid: file.uid || `${Date.now()}-${file.name}`, name: file.name, status: 'done', originFileObj: file } as any]);
                            return false;
                        }}
                        accept=".xlsx,.xls,.png,.doc,.docx,.ppt,.pptx,.txt,.pdf"
                    >
                        <Button icon='ui-upload' type='primary'>Adicionar Arquivo</Button>
                    </Upload>
                    {fileList.length > 0 && <Table
                        className='upload-multiple-table'
                        dataSource={fileList}
                        rowKey="uid"
                        pagination={false}
                        style={{ marginTop: 8 }}
                        columns={[
                            { title: 'Nome', dataIndex: 'name' },
                            {
                                title: 'Ações',
                                width: '10%',
                                render: (_, record: any) => (
                                    <Button icon='commons-trash' type='link' onClick={() => setFileList(prev => prev.filter(f => f.uid !== record.uid))} />
                                ),
                            },
                        ]}
                    />}
                    
                </div>}

                {!readOnly && <div style={{ marginBottom: 12 }}>
                    <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>
                        Data Limite de Resposta <span style={{ color: 'red' }}>*</span>
                    </label>
                    <DatePicker
                        locale={locale}
                        format="DD/MM/YYYY"
                        value={dataLimiteResposta}
                        style={{ width: '100%' }}
                        disabledDate={current => {
                            if (!dataLimiteEtapa) return false;
                            const limite = moment(dataLimiteEtapa).endOf('day');
                            return current && current > limite;
                        }}
                        onChange={date => setDataLimiteResposta(date)}
                    />
                </div>}

                {!readOnly && <div style={{ marginBottom: 12 }}>
                    <textarea
                        placeholder="Comentário *"
                        maxLength={500}
                        value={comentario}
                        onChange={e => setComentario(e.target.value)}
                        style={{ width: '100%', minHeight: 60, padding: 8, borderRadius: 4, border: '1px solid #d9d9d9' }}
                    />
                </div>}

                {!readOnly && <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="primary" onClick={() => handleDecisao('enviar_fornecedor')}>
                        Enviar ao Fornecedor
                    </Button>
                    <Button onClick={() => handleDecisao('enviar_gestor')}>
                        Devolver ao Gestor Técnico
                    </Button>
                </div>}
            </Modal>
        </>
    );
};

export default ModalAcoesCompradorParecerTecnico;
